// Utility functions for generating radiology report HTML
// Can be used for printing, preview, and other display purposes

import { RIS_API_BASE_URL } from '../../../../platform/app/src/custom/config/api.config';

export interface PatientInfo {
  patientName: string;
  patientId: string;
  patientAge: string;
  patientGender: string;
  studyDate: string;
  referringPhysician: string;
  studyDescription: string;
  /** RIS clinical history (worklist column); optional for older callers. */
  clinicalHistory?: string;
  modality?: string;
  bodyPart?: string;
  viewPosition?: string;
}

export interface FacilityInfo {
  facility_name?: string;
  facility_description?: string;
  facility_type?: string;
  contact_number?: string;
  email_id?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  letterhead_logo?: string | null;
}

export interface RadiologistData {
  name: string;
  qualification: string;
  registrationNumber: string;
  signature: string | null;
}

export interface ReportContentData {
  reportTitle: string;
  contentToPrint: string; // AI response or document template content
  findingsToPrint: string; // For regular templates
  impressionToPrint: string; // For regular templates
  reportContent: string; // Additional notes
  isAIMode: boolean;
  isDocumentTemplate: boolean;
}

export interface GenerateReportHtmlOptions {
  reportContent: ReportContentData;
  patientInfo: PatientInfo;
  facilityData: FacilityInfo | null;
  radiologistData: RadiologistData;
  apiBaseUrl?: string;
  defaultLogoUrl?: string;
  isPreview?: boolean; // If true, shows placeholder text instead of signature image
}

// Helper function to convert image URL to base64
const getBase64FromUrl = async (url: string): Promise<string> => {
  try {
    // Handle S3 URLs and other external URLs
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit', // Don't send credentials for external URLs
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', error);
    // Return empty string to indicate failure
    return '';
  }
};

// Build image URL from facility logo or signature path
const buildImageUrl = (
  imagePath: string | null | undefined,
  apiBaseUrl: string,
  defaultUrl: string,
  uploadPath: string
): string => {
  if (!imagePath) {
    return defaultUrl;
  }

  // Handle base64 data URLs
  if (imagePath.startsWith('data:')) {
    return imagePath;
  }

  // Handle full URLs (HTTP/HTTPS) - includes S3 URLs
  // S3 URLs can be: https://bucket.s3.region.amazonaws.com/path or presigned URLs
  // Also check for S3-specific patterns to ensure we catch all S3 URL formats
  if (
    imagePath.startsWith('http://') ||
    imagePath.startsWith('https://') ||
    imagePath.includes('s3.amazonaws.com') ||
    imagePath.includes('s3.') ||
    imagePath.includes('amazonaws.com')
  ) {
    // This is a full URL (S3 or other), return as-is
    return imagePath;
  }

  // Handle local upload paths
  if (imagePath.startsWith('/uploads/')) {
    const pathParts = imagePath.split('/');
    const filename = pathParts[pathParts.length - 1];
    const pathWithoutFilename = imagePath.substring(0, imagePath.lastIndexOf('/') + 1);
    const constructedUrl = `${apiBaseUrl}${pathWithoutFilename}${encodeURIComponent(filename)}`;
    return constructedUrl;
  }

  // Fallback: assume it's a filename and construct API URL
  const constructedUrl = `${apiBaseUrl}/uploads/${uploadPath}/${encodeURIComponent(imagePath)}`;
  return constructedUrl;
};

// Process facility data and return normalized facility info
const processFacilityData = (
  facilityData: FacilityInfo | null,
  defaultLogoUrl: string
): {
  name: string;
  description: string;
  type: string;
  phone: string;
  email: string;
  address: string;
  logo: string | null;
} => {
  if (facilityData) {
    return {
      name: facilityData.facility_name || 'IQLine',
      description:
        facilityData.facility_description ||
        facilityData.facility_type ||
        'Radiology Information System',
      type: facilityData.facility_type || 'Medical Imaging Solutions',
      phone: facilityData.contact_number
        ? `+91 ${facilityData.contact_number.replace(/^\+91\s*/, '')}`
        : '+1 8787988980',
      email: facilityData.email_id || 'info@iqline.com',
      address:
        [
          facilityData.address_line_1,
          facilityData.address_line_2,
          facilityData.city,
          facilityData.state,
          facilityData.pincode,
          facilityData.country,
        ]
          .filter(Boolean)
          .join(', ') || 'IQLine Medical Solutions',
      logo: facilityData.letterhead_logo || null,
    };
  }

  return {
    name: 'IQLine',
    description: 'Radiology Information System',
    type: 'Medical Imaging Solutions',
    phone: '+1 8787988980',
    email: 'info@iqline.com',
    address: 'IQLine Medical Solutions',
    logo: null,
  };
};

// Generate the HTML content for the report
const generateReportHtmlContent = (
  reportContent: ReportContentData,
  patientInfo: PatientInfo,
  facilityInfo: ReturnType<typeof processFacilityData>,
  radiologistData: RadiologistData,
  logoBase64: string,
  signatureBase64: string,
  currentDate: string,
  currentTime: string,
  isPreview: boolean = false
): string => {
  const {
    isAIMode,
    isDocumentTemplate,
    contentToPrint,
    findingsToPrint,
    impressionToPrint,
    reportContent: additionalNotes,
  } = reportContent;

  // Generate report body content based on mode
  let reportBodyContent = '';
  if (isAIMode) {
    // For AI mode, print the AI response
    reportBodyContent = contentToPrint
      ? `<div class="section-content">${contentToPrint}</div>`
      : '';
  } else if (isDocumentTemplate) {
    // For document templates, print the full content
    reportBodyContent = contentToPrint
      ? `<div class="section-content">${contentToPrint}</div>`
      : '';
  } else {
    // For regular templates, print findings and impression separately
    const findingsSection = findingsToPrint
      ? `<div class="section-title">FINDINGS</div><div class="section-content">${findingsToPrint}</div>`
      : '';
    const impressionSection = impressionToPrint
      ? `<div class="section-title">IMPRESSION:</div><div class="section-content">${impressionToPrint}</div>`
      : '';
    reportBodyContent = findingsSection + impressionSection;
  }

  // Additional notes section (only for non-AI mode)
  const additionalNotesSection =
    !isAIMode && additionalNotes
      ? `<div class="section-title">ADDITIONAL NOTES:</div><div class="section-content">${additionalNotes}</div>`
      : '';

  // Study description section
  const studyDescriptionSection =
    patientInfo.studyDescription && patientInfo.studyDescription !== 'N/A'
      ? `<div class="study-title">${patientInfo.studyDescription}</div>`
      : '';

  const clinicalHistorySection =
    patientInfo.clinicalHistory && patientInfo.clinicalHistory.trim()
      ? `<div class="section-title">CLINICAL HISTORY</div><div class="section-content">${patientInfo.clinicalHistory.trim()}</div>`
      : '';

  return `
    <html>
      <head>
        <title>${reportContent.reportTitle}</title>
        <style>
          @page {
            size: A4;
            margin: 0;
          }

          body {
            margin: 0;
            padding: 0;
          }

          #header {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            width: 100%;
            padding: 12mm 20mm 8mm 20mm;
            border-bottom: 3px solid #3AA9A0;
            background-color: #fff;
            z-index: 1000;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }


          #footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            padding: 6mm 20mm;
            border-top: 3px solid #3AA9A0;
            background-color: #fff;
            font-size: 9px;
            color: #555;
            z-index: 1000;
            box-shadow: 0 -2px 4px rgba(0,0,0,0.05);
          }

          .header-content {
            display: flex;
            align-items: flex-start;
            gap: 20px;
          }

          .header-logo {
            width: 150px;
            height: 80px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .header-logo svg {
            width: 100%;
            height: 100%;
          }

          .header-text {
            flex: 1;
            padding-top: 2px;
          }

          .header-title {
            font-size: 24px;
            font-weight: 700;
            color: #3AA9A0;
            margin-bottom: 4px;
            letter-spacing: 0.5px;
          }

          .header-subtitle {
            font-size: 13px;
            color: #666;
            margin-bottom: 2px;
            font-weight: 500;
          }

          .header-contact {
            font-size: 11px;
            color: #444;
            margin-top: 6px;
            line-height: 1.6;
          }

          .header-contact span {
            margin-right: 12px;
          }

          .footer-content {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-wrap: wrap;
            gap: 20px;
            text-align: center;
          }

          .footer-item {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #555;
          }

          .footer-item span:first-child {
            color: #3AA9A0;
            font-weight: 600;
          }

          .print-content {
            font-family: Arial, sans-serif;
            padding: 10mm 20mm 30mm 20mm;
            line-height: 1.4;
            color: #000;
            /* Add top margin to account for fixed header in preview mode */
            /* Header: 12mm top padding (~45px) + 8mm bottom padding (~30px) + logo (80px) + text content (~50px) + border ≈ 200px */
            margin-top: 150px;
          }

          .patient-info-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
            font-size: 12px;
          }

          .patient-info-table td {
            border: 1px solid #666;
            padding: 6px 10px;
          }

          .patient-info-table td:nth-child(odd) {
            background-color: #e8e8e8;
            font-weight: bold;
            width: 25%;
          }

          .patient-info-table td:nth-child(even) {
            width: 25%;
          }

          .study-title {
            font-size: 14px;
            font-weight: bold;
            margin: 20px 0 10px 0;
            text-transform: uppercase;
          }

          .section-title {
            font-weight: bold;
            margin: 15px 0 5px 0;
            font-size: 13px;
          }

          .section-content {
            margin: 5px 0 15px 0;
            font-size: 12px;
            white-space: pre-line;
            line-height: 1.6;
          }

          .signature-section {
            margin-top: 40px;
            font-size: 12px;
          }

          .signature-line {
            margin-top: 30px;
            margin-bottom: 5px;
          }

              @media print {
                body {
                  padding: 0;
                  margin: 0;
                }

                body.printing {
                  /* Add class for print mode */
                }

                #header {
                  position: fixed;
                  top: 0;
                  page-break-inside: avoid;
                }

                #footer {
                  position: fixed;
                  bottom: 0;
                  page-break-inside: avoid;
                }

                .print-content {
                  margin-top: 0; /* Reset margin for print */
                  padding-top: 50mm;
                  padding-bottom: 30mm;
                }

                .no-print {
                  display: none;
                }
              }
        </style>
      </head>
      <body>
        <!-- Header -->
        <div id="header">
          <div class="header-content">
            <div class="header-logo">
              ${logoBase64 ? `<img src="${logoBase64}" alt="${facilityInfo.name}" style="max-width: 150px; max-height: 80px; object-fit: contain;" onerror="this.style.display='none'" />` : ''}
            </div>
            <div class="header-text">
              <div class="header-title">${facilityInfo.name}</div>
              <div class="header-subtitle">${facilityInfo.description}</div>
              <div class="header-subtitle">${facilityInfo.type}</div>
              <div class="header-contact">
                <span>📞 ${facilityInfo.phone}</span>
                <span>✉️ ${facilityInfo.email}</span>
              </div>
              <div class="header-contact" style="margin-top: 3px;">
                📍 ${facilityInfo.address}
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div id="footer">
          <div class="footer-content">
            <div class="footer-item">
              <span>📞</span>
              <span>${facilityInfo.phone}</span>
            </div>
            <div class="footer-item">
              <span>✉️</span>
              <span>${facilityInfo.email}</span>
            </div>
            <div class="footer-item">
              <span>📍</span>
              <span>${facilityInfo.address}</span>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="print-content">
          <!-- Patient Information Table -->
          <table class="patient-info-table">
            <tr>
              <td>Patient Name:</td>
              <td>${patientInfo.patientName || 'N/A'}</td>
              <td>Patient ID:</td>
              <td>${patientInfo.patientId || 'N/A'}</td>
            </tr>
            <tr>
              <td>Patient Age:</td>
              <td>${patientInfo.patientAge || 'N/A'}</td>
              <td>Patient Gender:</td>
              <td>${patientInfo.patientGender || 'N/A'}</td>
            </tr>
            <tr>
              <td>Test Date:</td>
              <td>${patientInfo.studyDate || currentDate}</td>
              <td>Report Date:</td>
              <td>${currentDate}</td>
            </tr>
            <tr>
              <td>Referral Dr:</td>
              <td>${patientInfo.referringPhysician || 'N/A'}</td>
              <td>Report Time:</td>
              <td>${currentTime}</td>
            </tr>
          </table>

          <!-- Study Description/Title -->
          ${studyDescriptionSection}

          ${clinicalHistorySection}

          ${reportBodyContent}

          ${additionalNotesSection}

          <!-- Signature Section -->
          <div class="signature-section">
            ${
              isPreview
                ? '<div style="margin-bottom: 10px; font-style: italic; color: #666;">(Signature of the authorizing radiologist is shown here.)</div>'
                : signatureBase64
                  ? `<img src="${signatureBase64}" alt="Signature" style="max-width: 180px; max-height: 100px; margin-bottom: 5px;" onerror="this.style.display='none'" />`
                  : ''
            }
            <div>${radiologistData.name || 'Radiologist Name'}</div>
            <div>${radiologistData.qualification || 'MBBS, DMRD (Radiodiagnosis)'}</div>
            <div>Reg.No. ${radiologistData.registrationNumber || 'N/A'} HMC</div>
          </div>
        </div>
      </body>
    </html>
  `;
};

/**
 * Generate HTML string for radiology report
 * This function handles image loading and returns a complete HTML document
 * that can be used for printing, preview, or display purposes.
 *
 * @param options - Configuration options for report generation
 * @returns Promise that resolves to the complete HTML string
 */
export const generateReportHtml = async (options: GenerateReportHtmlOptions): Promise<string> => {
  const {
    reportContent,
    patientInfo,
    facilityData,
    radiologistData,
    apiBaseUrl = RIS_API_BASE_URL,
    defaultLogoUrl = 'https://iqline.co.in/wp-content/uploads/2023/11/iqline_newtran.png',
    isPreview = false,
  } = options;

  const currentDate = new Date().toLocaleDateString('en-GB');
  const currentTime = new Date().toLocaleTimeString('en-GB', { hour12: false });

  // Process facility data
  const facilityInfo = processFacilityData(facilityData, defaultLogoUrl);

  // Build image URLs
  const logoUrl = buildImageUrl(facilityInfo.logo, apiBaseUrl, defaultLogoUrl, 'logos');
  const signatureUrl = buildImageUrl(radiologistData.signature, apiBaseUrl, '', 'signatures');

  // Convert images to base64 to avoid CORS issues during printing
  // This works for both VM disk URLs and S3 URLs (if CORS is properly configured)
  let logoBase64 = '';
  let signatureBase64 = '';

  try {
    if (logoUrl && !logoUrl.startsWith('data:')) {
      // Try to convert to base64 (works for S3 URLs if CORS is enabled)
      const convertedLogo = await getBase64FromUrl(logoUrl);
      if (convertedLogo) {
        logoBase64 = convertedLogo;
      } else {
        // If base64 conversion failed, use the URL directly
        // This will work if the S3 bucket has proper CORS configuration
        logoBase64 = logoUrl;
      }
    } else {
      logoBase64 = logoUrl || defaultLogoUrl;
    }
  } catch (e) {
    console.error('Failed to load logo:', e);
    // Fallback to default logo if conversion fails
    logoBase64 = defaultLogoUrl;
  }

  try {
    // Check if signature URL exists and is not empty
    if (signatureUrl && signatureUrl.trim() !== '' && !signatureUrl.startsWith('data:')) {
      // Try to convert to base64 (works for S3 URLs if CORS is enabled)
      const convertedSignature = await getBase64FromUrl(signatureUrl);
      if (convertedSignature && convertedSignature.trim() !== '') {
        signatureBase64 = convertedSignature;
      } else {
        console.warn('Signature base64 conversion returned empty; using URL directly');
        // If base64 conversion failed, use the URL directly
        // This will work if the S3 bucket has proper CORS configuration
        signatureBase64 = signatureUrl;
      }
    } else {
      signatureBase64 = signatureUrl || '';
    }
  } catch (e) {
    console.error('Failed to load signature:', e);
    // If signature fails, try to use the URL directly as fallback
    if (signatureUrl && signatureUrl.trim() !== '') {
      signatureBase64 = signatureUrl;
    } else {
      signatureBase64 = '';
    }
  }

  // Generate and return the HTML
  return generateReportHtmlContent(
    reportContent,
    patientInfo,
    facilityInfo,
    radiologistData,
    logoBase64,
    signatureBase64,
    currentDate,
    currentTime,
    isPreview
  );
};
